import React, { useLayoutEffect, useMemo, useRef } from 'react'
import { css, cx } from '@linaria/core'
import { Row, MenuOverlay, ListItem, useMenuOverlay, colors } from '@fs/zion-ui'
import { Link } from '@fs/zion-router'
import { trackLink } from '@fs/zion-analytics'
import { baseUrl } from '@fs/zion-config'
import { ArrowCaret } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'
import useResponsiveOverflow from './hooks/useResponsiveOverflow'
import useHeaderBreakpoint from './hooks/useHeaderBreakpoint'

const navCss = css`
  padding: 10px;
  background: ${colors.gray00};
  border-top: 1px solid ${colors.gray10};
`

// Outer Row frame (no width constraints)
const frameRowCss = css`
  display: flex;
  align-items: center;
  gap: 2px;
`

// Inner items container row (constrained to Cell width)
const rowCss = css`
  display: flex;
  align-items: center;
  gap: 2px;
  position: relative;
  width: 100%;
  min-width: 0;
`

// Added while calculating; used to alter visibility behavior for hidden items
const rowCalculatingCss = css``

const navItemWrapperCss = css`
  display: flex;
  align-items: center;
  white-space: nowrap;
`

// Hide overflow items visually but keep them in DOM; while calculating, keep layout impact
const visuallyHiddenCss = css`
  .${rowCss}:not(.${rowCalculatingCss}) & {
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    pointer-events: none;
    position: absolute;
    white-space: nowrap;
    width: 1px;
  }

  .${rowCalculatingCss} & {
    visibility: hidden;
  }
`

const linkCss = css`
  display: inline-flex;
  gap: 0.125rem;
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 4px;
  cursor: pointer;
  font: inherit;
  line-height: inherit;
  padding: 4px 10px;
  text-decoration: none;
  white-space: nowrap;
  font-size: 14px;
`

const visibleLinkCss = cx(
  linkCss,
  css`
    &:hover,
    &[aria-current='page'] {
      background: #e1edc1;
    }

    &,
    &:visited,
    &:active,
    &:hover {
      color: ${colors.gray60};
    }
  `
)

const slotCss = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

function normalizeSubHeaderContent(items) {
  if (!Array.isArray(items) || items.length === 0) return []

  return items
    .filter((item) => item && typeof item.url === 'string' && item.url.length > 0)
    .map((item, index) => {
      const text = item.text || item.key || item.url

      return {
        id: item.id || item.key || item.url || String(index),
        text,
        url: item.url,
        target: item.target,
        active: Boolean(item.active),
      }
    })
}

function buildUrl(url, external) {
  return `${external && !url.startsWith('https:') ? baseUrl : ''}${url}`
}

function SubHeaderNavItem({ item, external }) {
  const handleClick = () => {
    const label = item.id || item.url || item.text
    trackLink(null, item.url, 'click_action', `subheader_nav:${label}`)
  }

  return (
    <Link
      to={buildUrl(item.url, external)}
      className={visibleLinkCss}
      aria-current={item.active ? 'page' : undefined}
      onClick={handleClick}
      external
      noLinkStyles
      target={item.target}
    >
      {item.text}
    </Link>
  )
}

export default function SubHeader({
  external = false,
  className = '',
  items,
  startSlot: StartSlot,
  endSlot: EndSlot,
  ...props
}) {
  // Stay in sync with header's dynamic content-based breakpoint
  const headerBreakpoint = useHeaderBreakpoint()
  const content = useMemo(() => normalizeSubHeaderContent(items), [items])
  const hasContent = content.length > 0
  const moreButtonRef = useRef(null)

  // Responsive overflow management
  const { itemsContainerRef, lastVisibleIndex, recalculateVisibleItems } = useResponsiveOverflow(
    content,
    rowCalculatingCss,
    moreButtonRef
  )
  const hasOverflow = content.length > 0 && lastVisibleIndex < content.length - 1
  const moreMenuOverlay = useMenuOverlay()

  const { t } = useTranslation()

  useLayoutEffect(() => {
    if (hasOverflow) {
      recalculateVisibleItems()
    }
  }, [hasOverflow, recalculateVisibleItems])

  if (headerBreakpoint === 'mobile' || !hasContent) {
    return null
  }

  return (
    <nav className={cx(navCss, className)} {...props}>
      <Row alignY="middle" gutters="none" className={frameRowCss}>
        {StartSlot && <div className={slotCss}>{typeof StartSlot === 'function' ? <StartSlot /> : StartSlot}</div>}

        <div growX ref={itemsContainerRef} className={rowCss}>
          {content.map((item, index) => (
            <span
              key={item.id || item.url}
              data-nav-item
              className={cx(navItemWrapperCss, index > lastVisibleIndex ? visuallyHiddenCss : undefined)}
            >
              <SubHeaderNavItem item={item} external={external} />
            </span>
          ))}

          {/* More dropdown for overflow items */}
          {hasOverflow && (
            <MenuOverlay
              menuButton={
                <button
                  ref={moreButtonRef}
                  type="button"
                  className={visibleLinkCss}
                  onClick={moreMenuOverlay.handleClick}
                >
                  {t('subheader.more', 'More')} <ArrowCaret direction="down" size="xs" />
                </button>
              }
            >
              {content.slice(lastVisibleIndex + 1).map((item) => (
                <ListItem
                  key={item.id || item.url}
                  primaryText={item.text}
                  to={buildUrl(item.url, external)}
                  selected={item.active}
                  external
                  target={item.target}
                />
              ))}
            </MenuOverlay>
          )}
        </div>

        {EndSlot && <div className={slotCss}>{typeof EndSlot === 'function' ? <EndSlot /> : EndSlot}</div>}
      </Row>
    </nav>
  )
}
